import { useState, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { PlugZap, UsersRound, LogOut } from 'lucide-react';
import { IntegrationsSidebar } from '../integrations/IntegrationsSidebar';
import { WorkspaceSettings } from '../workspace/WorkspaceSettings';
import { createGTMTracking, getGTMAttributes } from '../../utils/gtm/gtm-tracking';
import { signupIntent } from '../../services/auth.service';
import { Button } from '@/components/ui/atoms/Button';

interface UserAccountMenuProps {
  className?: string;
}

export default function UserAccountMenu({ className = '' }: UserAccountMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isIntegrationsSidebarOpen, setIsIntegrationsSidebarOpen] = useState(false);
  const [isWorkspaceSettingsOpen, setIsWorkspaceSettingsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const { user, logout } = useAuth0();

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node) &&
          buttonRef.current && !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen]);

  const handleLogout = () => {
    // Clear cached signup intent so a re-login doesn't re-finalize a
    // stale plan choice.
    signupIntent.clear();
    // Move the URL to "/" before logout so ProtectedRouteWrapper doesn't
    // bounce us to /login during the brief window when local auth state
    // is cleared but Auth0's redirect hasn't fired yet.
    window.history.replaceState(null, '', '/');
    logout({
      logoutParams: {
        // No trailing slash — Auth0 matches Allowed Logout URLs exactly.
        returnTo: window.location.origin,
      },
    });
  };

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleMenu();
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const renderAvatar = () => {
    if (user?.picture) {
      return (
        <img
          src={user.picture}
          alt={user.name || 'User avatar'}
          className="w-8 h-8 rounded-full object-cover border-2 border-gradient-purple"
        />
      );
    }

    return (
      <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center text-sm font-medium">
        {user?.name ? getInitials(user.name) : 'U'}
      </div>
    );
  };

  const renderDropdown = () => (
    <div
      ref={menuRef}
      className={`absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-100 z-50 ${
        isOpen ? 'block' : 'hidden'
      }`}
    >
      <div className="py-2">
        {/* User Info */}
        <div className="px-4 py-2.5 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            {renderAvatar()}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user?.name || 'User'}
              </p>
              <p className="text-sm text-gray-500 truncate">
                {user?.email}
              </p>
            </div>
          </div>
        </div>

        {/* Links */}
        <div className="py-1">
          <Button
            type="text"
            intent="secondary"
            size="sm"
            onClick={() => {
              setIsOpen(false);
              setIsIntegrationsSidebarOpen(true);
            }}
            icon={<PlugZap />}
            label="Integrations"
            className="w-full !justify-start rounded-none px-4 py-2.5"
          />
          <Button
            type="text"
            intent="secondary"
            size="sm"
            onClick={() => {
              setIsOpen(false);
              setIsWorkspaceSettingsOpen(true);
            }}
            icon={<UsersRound />}
            label="Workspace"
            className="w-full !justify-start rounded-none px-4 py-2.5"
          />
        </div>

        {/* Sign Out */}
        <Button
          type="text"
          intent="secondary"
          size="sm"
          onClick={handleLogout}
          icon={<LogOut />}
          label="Sign Out"
          className="w-full !justify-start rounded-none px-4 py-2.5"
          {...getGTMAttributes(createGTMTracking('LOGOUT', { origin: 'user_menu' }))}
        />
      </div>
    </div>
  );

  if (!user) {
    return null;
  }

  return (
    <>
      <div className={`relative ${className}`}>
        {/* Avatar Button */}
        <button
          ref={buttonRef}
          onClick={toggleMenu}
          onKeyDown={handleKeyDown}
          className="flex items-center justify-center w-8 h-8 rounded-full hover:ring-2 hover:ring-white/20 focus:ring-2 focus:ring-white/20 focus:outline-none transition-all duration-150"
          aria-label="User account menu"
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          {renderAvatar()}
        </button>

        {/* Dropdown Menu */}
        {renderDropdown()}
      </div>

      {/* Integrations Sidebar */}
      <IntegrationsSidebar
        isOpen={isIntegrationsSidebarOpen}
        onClose={() => setIsIntegrationsSidebarOpen(false)}
      />

      {/* Workspace Settings */}
      <WorkspaceSettings
        isOpen={isWorkspaceSettingsOpen}
        onClose={() => setIsWorkspaceSettingsOpen(false)}
      />
    </>
  );
}
